import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Minus, Plus, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import { ShareButtons } from "@/components/ShareButtons";
import { fetchProduct, createCheckout } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);
  const [dedication, setDedication] = useState("");

  useEffect(() => {
    fetchProduct(id)
      .then(setProduct)
      .catch(() => { toast.error("Product not found."); navigate("/store"); });
  }, [id, navigate]);

  const buyNow = async () => {
    setBuying(true);
    try {
      const { checkout_url } = await createCheckout(
        [{ lookup_key: product.lookup_key, quantity: qty, personalization: product.personalize ? dedication : null }],
        window.location.origin
      );
      window.location.href = checkout_url;
    } catch {
      toast.error("Unable to start checkout.");
      setBuying(false);
    }
  };

  if (!product) {
    return <div className="min-h-screen bg-cream pt-40 flex justify-center"><span className="font-editorial italic text-2xl text-emerald">Loading…</span></div>;
  }

  return (
    <main className="bg-cream min-h-screen pt-32 pb-28" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/store" className="inline-flex items-center gap-2 font-body text-sm text-ink/60 hover:text-emerald mb-12 transition-colors" data-testid="back-to-store">
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>

        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="relative bg-surface border border-line aspect-[3/4] flex items-center justify-center overflow-hidden">
                {product.merch ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-emerald">
                    <Logo className="w-24 h-24" />
                    <p className="font-display text-2xl text-cream px-6 text-center">{product.name.replace("Love Genome ", "")}</p>
                    <span className="eyebrow text-gold">Love Genome™ Merch</span>
                  </div>
                ) : (
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="detail-image" />
                )}
                {product.badge && (
                  <span className="absolute top-6 left-6 bg-emerald text-cream text-xs uppercase tracking-widest px-4 py-2">{product.badge}</span>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:pl-8">
            <Reveal>
              <p className="eyebrow text-emerald mb-4">{product.format}</p>
              <h1 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight mb-4">
                {product.name.replace("The Love Genome — ", "")}
              </h1>
              <p className="font-editorial italic text-2xl text-emerald mb-8">{product.tagline}</p>
              <p className="font-display text-4xl text-ink mb-10" data-testid="detail-price">${product.price.toFixed(2)}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="font-body text-ink/70 text-lg leading-relaxed mb-10">{product.description}</p>

              <ul className="grid sm:grid-cols-2 gap-4 mb-12">
                {product.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 font-body text-ink/80">
                    <span className="w-6 h-6 bg-surface border border-line flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-emerald" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>

            {product.personalize && (
              <Reveal delay={0.12}>
                <div className="border border-gold/40 bg-gold/5 p-6 mb-2 mt-2" data-testid="personalization-block">
                  <label className="eyebrow text-emerald block mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Personalize this signed copy
                  </label>
                  <input
                    value={dedication}
                    onChange={(e) => setDedication(e.target.value)}
                    maxLength={120}
                    placeholder="Name to dedicate the book to (optional)"
                    data-testid="dedication-input"
                    className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink placeholder:text-ink/30 transition-colors"
                  />
                  <p className="font-body text-xs text-ink/45 mt-2">
                    Felicia will personally inscribe this name inside your copy.
                  </p>

                  <div className="mt-5 relative bg-cream border border-line p-8 text-center overflow-hidden" data-testid="dedication-preview">
                    <div className="absolute inset-3 border border-gold/30 pointer-events-none" />
                    <div className="relative">
                      <Logo className="w-10 h-10 mx-auto mb-4" />
                      <p className="font-editorial italic text-ink/50 text-sm">This copy is lovingly inscribed for</p>
                      <p className="font-display text-3xl text-emerald my-3 min-h-[2.5rem] break-words" data-testid="dedication-preview-name">
                        {dedication.trim() || "—"}
                      </p>
                      <p className="font-editorial italic text-ink/60 text-sm">with love and gratitude,</p>
                      <p className="mt-1 text-gold" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", fontStyle: "italic" }}>
                        Felicia T. Johnson
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            <Reveal delay={0.15}>
              <div className="flex flex-wrap items-center gap-4 border-t border-line pt-10">
                <div className="flex items-center border border-line">
                  <button className="p-3.5 hover:bg-surface" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec"><Minus className="w-4 h-4" /></button>
                  <span className="px-5 font-body" data-testid="qty-value">{qty}</span>
                  <button className="p-3.5 hover:bg-surface" onClick={() => setQty((q) => q + 1)} data-testid="qty-inc"><Plus className="w-4 h-4" /></button>
                </div>
                <button
                  onClick={() => { addItem(product, qty, product.personalize ? dedication : null); toast.success("Added to cart"); }}
                  className="btn-outline flex-1 min-w-[160px]"
                  data-testid="detail-add-to-cart"
                >
                  Add to Cart
                </button>
                <button onClick={buyNow} disabled={buying} className="btn-emerald flex-1 min-w-[160px] disabled:opacity-60" data-testid="detail-buy-now">
                  {buying ? "Redirecting…" : "Buy Now"}
                </button>
              </div>
              <p className="flex items-center gap-2 font-body text-sm text-ink/50 mt-6">
                <ShieldCheck className="w-4 h-4 text-emerald" /> Secure checkout with Stripe · PayPal coming soon
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-line pt-6">
                <span className="eyebrow text-ink/40">Share</span>
                <ShareButtons title={`${product.name} — The Love Genome™`} />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
